import Raven from 'raven-js';
import { useCallback, useMemo } from 'react';
import CMPDeleteModal from 'content-management-actions-lib/modals/DeleteModal';
import { CONTENT_PLATFORM_PROPERTY_NAME, getContentPropertyValue, getPropertyValue, useContentObjectType, ContentObjectTypes } from 'content-management-data-lib';
import { CATEGORY_IDS } from 'ContentUtils/constants/Categories';
import Routes from 'ContentUtils/Routes';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { PAGE_PROPERTY_NAMES } from '../../constants/propertyNames';
import { useFetchPersonalizationVariants } from '../../hooks/useFetchPersonalizationVariants';
import { useDeletePersonalizedContent } from '../../hooks/useDeletePersonalizedContent';
import { isPersonalizationDefault, isContentObjectPersonalized } from '../../utils/personalization';
import { useContentActions } from 'content-management-actions-lib/providers/ContentActionsProvider';
import { PAGE_ACTION_IDS } from '../../constants/actionIds';
import { Gates } from '../../constants/Gates';
import { ConfirmModal } from 'foundations-components/transitional/components/ConfirmModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Header = ({
  isDeletingDefault
}) => isDeletingDefault ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
  message: "page-lib.personalization.deleteActionOverrideModal.header.personalizationDefault"
}) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
  message: "page-lib.personalization.deleteActionOverrideModal.header.personalizationVariant"
});
const ModalBody = ({
  isDeletingDefault,
  personalizationVariants,
  categoryId
}) => {
  if (isDeletingDefault) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "page-lib.personalization.deleteActionOverrideModal.body.personalizationDefault"
      }), /*#__PURE__*/_jsx(List, {
        styled: true,
        children: personalizationVariants.map(variant => {
          if (variant.personalizationVariantStatus === 'DEFAULT') {
            return null;
          }
          return /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Link, {
              external: true,
              href: Routes.details(variant.id, categoryId),
              children: variant.name
            })
          }, variant.id);
        })
      })]
    });
  }
  const personalizationDefaultPage = personalizationVariants.find(variantPage => variantPage.personalizationVariantStatus === 'DEFAULT');
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "page-lib.personalization.deleteActionOverrideModal.body.personalizationVariant_jsx",
    elements: {
      Link,
      strong: ({
        children
      }) => /*#__PURE__*/_jsx(Text, {
        as: "strong",
        children: children
      })
    },
    options: {
      linkProps: {
        external: true,
        href: Routes.details(personalizationDefaultPage.id, categoryId)
      },
      pageName: personalizationDefaultPage.name
    }
  });
};
const DeletePersonalizedContentModal = ({
  crmObjects,
  onClose
}) => {
  const {
    onActionSuccess,
    onActionError
  } = useContentActions();
  const {
    contentObjectType
  } = useContentObjectType();
  const isLandingPage = contentObjectType === ContentObjectTypes.LANDING_PAGE;
  const categoryId = isLandingPage ? CATEGORY_IDS.LANDING_PAGE : CATEGORY_IDS.SITE_PAGE;
  // NOTE: bulk deletion of personalized content is currently disabled, so we'll only ever have one crmObject here.
  const targetCrmObject = crmObjects[0];
  const targetContentId = getPropertyValue(targetCrmObject, CONTENT_PLATFORM_PROPERTY_NAME.ID);
  const personalizationStrategyId = getContentPropertyValue(targetCrmObject, PAGE_PROPERTY_NAMES.PERSONALIZATION_STRATEGY_ID);
  const isDeletingDefault = isPersonalizationDefault(targetCrmObject);
  const {
    personalizationVariants,
    loading: isFetchingVariantsLoading,
    error: __error
  } = useFetchPersonalizationVariants(targetContentId);
  const {
    deletePersonalizedContent,
    deleteIsLoading,
    deleteError,
    isDeleteReady
  } = useDeletePersonalizedContent({
    contentId: targetContentId,
    personalizationStrategyId,
    isDeletingDefault
  });
  const isDeleteDisabled = useMemo(() => {
    return isFetchingVariantsLoading || !isDeleteReady;
  }, [isFetchingVariantsLoading, isDeleteReady]);
  const onConfirmDelete = useCallback(async () => {
    try {
      await deletePersonalizedContent();
      onClose();
      onActionSuccess({
        actionId: PAGE_ACTION_IDS.DELETE_PERSONALIZED_CONTENT,
        data: {
          completedResults: [targetCrmObject],
          actionData: {
            isDeletingDefault
          }
        }
      });
    } catch (error) {
      onActionError({
        actionId: PAGE_ACTION_IDS.DELETE_PERSONALIZED_CONTENT
      });
      Raven.captureMessage('Delete personalized content failed', {
        extra: {
          error,
          deleteError
        }
      });
    }
  }, [deletePersonalizedContent, onClose, onActionSuccess, targetCrmObject, isDeletingDefault, deleteError, onActionError]);
  return /*#__PURE__*/_jsx(ConfirmModal, {
    ConfirmButton: LoadingButton,
    confirmButtonProps: {
      loading: deleteIsLoading,
      disabled: isDeleteDisabled,
      onClick: onConfirmDelete,
      use: 'danger'
    },
    confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-actions-lib.modals.delete.buttons.delete"
    }),
    "data-test-id": `delete-personalized-${isDeletingDefault ? 'default' : 'variant'}-page-modal`,
    description: isFetchingVariantsLoading ? /*#__PURE__*/_jsx(LoadingSpinner, {
      justifySelf: "center"
    }) : /*#__PURE__*/_jsx(ModalBody, {
      isDeletingDefault: isDeletingDefault,
      personalizationVariants: personalizationVariants,
      categoryId: categoryId
    }),
    matcherLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "page-lib.personalization.deleteActionOverrideModal.matchTextConfirm"
    }),
    match: I18n.text('content-management-actions-lib.modals.delete.deleteMatch.text'),
    message: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        children: /*#__PURE__*/_jsx(Header, {
          isDeletingDefault: isDeletingDefault
        })
      })]
    }),
    RejectButton: Button,
    rejectButtonProps: {
      onClick: onClose,
      use: 'tertiary-light'
    },
    rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-actions-lib.modals.delete.buttons.cancel"
    }),
    use: "danger"
  });
};
const DeletePageModal = modalProps => {
  const {
    crmObjects,
    gates
  } = modalProps;
  const isUsingPersonalization = crmObjects.some(isContentObjectPersonalized);

  // Note, in case of future discrepancies: CMPDeleteModal is not using ConfirmModal, but this modal is
  if (isUsingPersonalization) {
    return /*#__PURE__*/_jsx(DeletePersonalizedContentModal, Object.assign({}, modalProps));
  }
  if (gates.includes(Gates.SOFT_DELETE_PAGES)) {
    const renderBodyContent = ({
      isDeletingMultiLangGroup,
      count,
      contentName,
      contentObjectTypePluralNoun
    }) => {
      if (isDeletingMultiLangGroup) {
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "page-lib.softDeletePage.body.mlangGroup"
        });
      }
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "page-lib.softDeletePage.body",
        options: {
          count,
          contentName,
          contentObjectTypePluralNoun
        }
      });
    };
    return /*#__PURE__*/_jsx(CMPDeleteModal, Object.assign({}, modalProps, {
      renderBodyContent: renderBodyContent
    }));
  }
  return /*#__PURE__*/_jsx(CMPDeleteModal, Object.assign({}, modalProps));
};
export default DeletePageModal;